// EPS-4414: this file has pre-existing @typescript-eslint/no-explicit-any, no-unsafe-*, and
// no-console violations unrelated to the hsp entries added by this ticket — CI's changed-files
// lint gate (.github/workflows/lint.yml) surfaces the whole file once any line changes.
// Retyping the dynamic brand-module loader is out of scope here — see concerns.md.
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, no-console, no-useless-escape */
import { Injectable } from '@angular/core';
import type { SitesDictionary } from 'libs/common/src/lib/accessibility/description-siblingSites.dictionary';
import { brandSites } from 'libs/common/src/lib/accessibility/description-siblingSites.dictionary';
import { EnvService } from '../env/env.service';

@Injectable({
  providedIn: 'root',
})
export class AccessibilityService {
  public brandSites: SitesDictionary[] = brandSites;
  private navLinksPromise: Promise<void>;
  headerNavLinks: ParsedNavLink[] = [];
  footerNavLinks: ParsedNavLink[] = [];

  constructor(public env: EnvService) {
    this.navLinksPromise = this.parseNavLinks();
  }

  get siblingSites(): string[] {
    return this.env.get.brandConfig.siblingSites;
  }

  /**
   * Gets the aria-label value to the anchor element
   * Removing dashes and slashes from a link
   * Validating type of url link
   * @param link The string with the URL
   * @param html The string with html code
   */
  getAriaLabel(link: string, html?: string): string {
    if (link === '/') {
      return 'Home';
    }
    if (link?.startsWith('http')) {
      return this.removeHTMLTags(html || '');
    }

    const sectionList = link?.split('/');
    if (sectionList[0] === '') {
      sectionList.shift();
    }

    sectionList.pop();
    sectionList.push(sectionList[0]);
    sectionList.shift();

    const firstReplae = sectionList.toString().replace(/\-/gi, ' ');
    const ariaLabel = firstReplae.replace(/\,/gi, ' ');
    return ariaLabel.replace(/(^\w{1})|(\s+\w{1})/g, (l) => l.toUpperCase());
  }

  /**
   * Remove html code from a string
   * @param html The string with html code
   */
  removeHTMLTags(html: string): string {
    if (html === null || html === '') {
      return '';
    }
    html = html?.toString();
    return html?.replace(/(<([^>]+)>)/gi, '');
  }

  /**
   * Gets the aria-labelledby value to the anchor element
   * Removing html code from a label
   * @param label The string with visible label link
   */
  getAriaLabelledby(label: string): string {
    const textLabel = label?.replace(/(<([^>]+)>)/gi, '');
    const withoutSpaces = textLabel?.replace(/\s/g, '-').toLowerCase();
    const cleanText = withoutSpaces?.toString().replace(/\--/gi, '-');
    return cleanText;
  }

  /**
   * Gets context information value for hidden element
   * @param link string with the URL link
   * @param html The string with html code
   */
  getCompleteTextContextForButton(link: string, html?: string): string {
    if (this.isNavSite(link)) {
      const navLabel = this.getNavLinkLabel(link);
      if (navLabel.length > 0) {
        return navLabel + ' Navigation Link';
      }
    }
    if (this.isUrlSite(link)) {
      return this.getSiteDescription(link);
    }
    return this.getAriaLabel(link, html);
  }

  getTextContext(link: string, label?: string): string {
    if (link?.startsWith('http')) {
      return this.getContextToDomainLink(link, label);
    }
    return this.getCompleteTextContextForButton(link);
  }

  isUrlSite(link: string): boolean {
    return this.siblingSites?.some((site) => link.includes(site));
  }

  getContextToDomainLink(link: string, label?: string): string {
    if (this.isUrlSite(link)) {
      return this.getSiteDescription(link);
    }
    return this.getAriaLabel(link, label);
  }

  getSiteDescription(urlDomain: string): string {
    return this.brandSites.find((site) => site.domain === urlDomain)?.description || '';
  }

  getTextContextForLinkInContent(link: string): string {
    if (this.brandSites.find((site) => site.domain === link)?.description) {
      return this.getSiteDescription(link);
    }
    if (link?.startsWith('http')) {
      return this.getAriaLabelToContentRedactor(link);
    }
    return this.getCompleteTextContextForButton(link);
  }

  getAriaLabelToContentRedactor(link: string): string {
    let sectionList = link?.split('/');
    sectionList = sectionList.filter((value) => value !== '' && !value.startsWith('http') && !value.startsWith('www.'));

    sectionList.push(sectionList[0]);
    sectionList.shift();

    const firstReplace = sectionList.toString().replace(/\-/gi, ' ');
    const ariaLabel = firstReplace.replace(/\,/gi, ' ');
    return ariaLabel.replace(/(^\w{1})|(\s+\w{1})/g, (l) => l.toUpperCase());
  }

  isNavSite(link: string): boolean {
    return (
      this.headerNavLinks?.some((navLink) => navLink.url === link) ||
      this.footerNavLinks?.some((navLink) => navLink.url === link)
    );
  }

  getNavLinkLabel(link: string): string {
    const headerLink = this.headerNavLinks?.find((navLink) => navLink.url === link);
    if (headerLink) return headerLink.label;

    const footerLink = this.footerNavLinks?.find((navLink) => navLink.url === link);
    return footerLink ? footerLink.label : '';
  }

  public async waitForNavLinks(): Promise<void> {
    return this.navLinksPromise;
  }

  public async parseNavLinks(): Promise<void> {
    try {
      const brand = this.env.get.brandConfig?.id?.toLowerCase() || 'ace';

      // Parse header data
      await this.parseHeaderData(brand);

      // Parse footer data
      await this.parseFooterData(brand);
    } catch (error) {
      // Silently handle errors - this is an enhancement, not critical functionality
      this.headerNavLinks = [];
      this.footerNavLinks = [];
    }
  }

  private async parseHeaderData(brand: string): Promise<void> {
    this.headerNavLinks = [];

    try {
      const brandModules: Record<string, () => Promise<any>> = {
        ace: () => import('@core-mkt/brands/ace/src/app/components/header/header/header-data'),
        aa: () => import('@core-mkt/brands/aa/src/app/components/header/header/header-data'),
        ins: () => import('@core-mkt/brands/ins/src/app/components/header/header/header-data'),
        mtg: () => import('@core-mkt/brands/mtg/src/app/components/header/header/header-data'),
        hsp: () => import('@core-mkt/brands/hsp/src/app/components/header/header/header-data'),
        aarp: () => import('@core-mkt/brands/aarp/src/app/components/header/header/header-data'),
        dec: () => import('@core-mkt/brands/dec/src/app/components/header/header/header-data'),
      };

      if (brandModules[brand]) {
        const headerModule = await brandModules[brand]();

        switch (brand) {
          case 'ace':
          case 'aa':
          case 'ins':
          case 'mtg':
          case 'hsp':
            this.parseStandardHeaderFormat(headerModule);
            break;
          case 'aarp':
            this.parseAarpHeaderFormat(headerModule);
            break;
          case 'dec':
            this.parseDecHeaderFormat(headerModule);
            break;
          default:
            // Try standard format as fallback
            this.parseStandardHeaderFormat(headerModule);
            break;
        }
      }
    } catch (error) {
      console.log(`Error loading header data for brand ${brand}:`, error);
      // Brand doesn't have header data or format not recognized
    }
  }

  private parseStandardHeaderFormat(headerModule: any): void {
    const headerData = headerModule.headerData || [];
    const myAccountData = headerModule.myAccountData || [];
    const allData = [...headerData, ...myAccountData];

    for (const link of allData) {
      if (link.isDropDown && link.ddList) {
        for (const ddLink of link.ddList) {
          this.headerNavLinks.push({
            label: this.removeHTMLTags(ddLink.linkLabel || ddLink.linkText || ''),
            url: ddLink.linkUrl || ddLink.href || '',
          });
        }
      } else if (link.linkUrl || link.href) {
        this.headerNavLinks.push({
          label: this.removeHTMLTags(link.linkLabel || link.linkText || ''),
          url: link.linkUrl || link.href || '',
        });
      }
    }
  }

  private parseAarpHeaderFormat(headerModule: any): void {
    const headerData = headerModule.headerData;
    if (!headerData) return;

    // AARP header data is an object with different properties containing link arrays or objects
    for (const [, value] of Object.entries(headerData)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item.linkUrl) {
            this.headerNavLinks.push({
              label: this.removeHTMLTags(item.linkLabel || item.linkText || ''),
              url: item.linkUrl,
            });
          }
        }
      } else if (value && typeof value === 'object' && (value as any).linkUrl) {
        const linkItem = value as any;
        this.headerNavLinks.push({
          label: this.removeHTMLTags(linkItem.linkLabel || linkItem.linkText || ''),
          url: linkItem.linkUrl,
        });
      }
    }
  }

  private parseDecHeaderFormat(headerModule: any): void {
    const headerData = headerModule.headerData || [];

    for (const item of headerData) {
      if (item.href) {
        this.headerNavLinks.push({
          label: this.removeHTMLTags(item.text || ''),
          url: item.href,
        });
      }
    }

    // Also parse complex state-specific data if available
    const newHeaderData = headerModule.newHeaderData;
    if (newHeaderData) {
      for (const [, stateData] of Object.entries(newHeaderData)) {
        const stateInfo = stateData as any;
        if (stateInfo.menuItems) {
          for (const item of stateInfo.menuItems) {
            if (item.href) {
              this.headerNavLinks.push({
                label: this.removeHTMLTags(item.text || ''),
                url: item.href,
              });
            }
          }
        }
      }
    }
  }

  private async parseFooterData(brand: string): Promise<void> {
    this.footerNavLinks = [];

    try {
      const brandModules: Record<string, () => Promise<any>> = {
        ace: () => import('@core-mkt/brands/ace/src/app/components/footer/footer/footer-data'),
        aa: () => import('@core-mkt/brands/aa/src/app/components/footer/footer/footer-data'),
        ins: () => import('@core-mkt/brands/ins/src/app/components/footer/footer/footer-data'),
        mtg: () => import('@core-mkt/brands/mtg/src/app/components/footer/footer/footer-data'),
        hsp: () => import('@core-mkt/brands/hsp/src/app/components/footer/footer/footer-data'),
        aarp: () => import('@core-mkt/brands/aarp/src/app/components/footer/footer/footer-data'),
      };

      if (brandModules[brand]) {
        const footerModule = await brandModules[brand]();
        switch (brand) {
          case 'ace':
          case 'aa':
          case 'ins':
          case 'mtg':
          case 'hsp':
            this.parseStandardFooterFormat(footerModule);
            break;
          case 'aarp':
            this.parseAarpFooterFormat(footerModule);
            break;
          case 'dec':
            // DEC footer is commented out, skip
            break;
          default:
            // Try standard format as fallback
            this.parseStandardFooterFormat(footerModule);
            break;
        }
      }
    } catch (error) {
      // Brand doesn't have footer data or format not recognized
      console.log(`Error loading footer data for brand ${brand}:`, error);
    }
  }

  private parseStandardFooterFormat(footerModule: any): void {
    const footerData = footerModule.footerData;
    if (!footerData) return;

    // Standard footer format has multiple arrays of links
    for (const [, value] of Object.entries(footerData)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          const url = item.linkUrl || item.link || item.href;
          const label = item.linkText || item.linkLabel || item.label || item.text;

          if (url && label) {
            this.footerNavLinks.push({
              label: this.removeHTMLTags(label),
              url,
            });
          }
        }
      }
    }
  }

  private parseAarpFooterFormat(footerModule: any): void {
    const footerData = footerModule.footerData;
    if (!Array.isArray(footerData)) return;

    for (const item of footerData) {
      if (item.linkUrl) {
        this.footerNavLinks.push({
          label: this.removeHTMLTags(item.linkLabel || item.linkText || ''),
          url: item.linkUrl,
        });
      }
    }
  }
}

export interface ParsedNavLink {
  label: string;
  url: string;
}
